import type {
  ChangeSet,
  AppointmentModel,
} from '@devexpress/dx-react-scheduler';
import { ViewState, EditingState } from '@devexpress/dx-react-scheduler';
import {
  EditRecurrenceMenu,
  Scheduler,
  Appointments,
  ViewSwitcher as DxViewSwitcher,
  Toolbar,
  WeekView,
  MonthView,
  DayView,
  AppointmentTooltip,
  DateNavigator as DxDateNavigator,
  TodayButton as DxTodayButton,
  ConfirmationDialog as DxConfirmationDialog,
  CurrentTimeIndicator as DxCurrentTimeIndicator,
  AppointmentForm,
} from '@devexpress/dx-react-scheduler-material-ui';
import { useTheme } from '@mui/material/styles';
import * as Sentry from '@sentry/gatsby';
import { addMinutes } from 'date-fns/esm';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useIntl, defineMessages } from 'react-intl';
import { RRule } from 'rrule';

import ContentLoader from '@components/ContentLoader/';
import Icon from '@components/Icon/Icon';
import {
  HorizontalFlex,
  OuterContainer,
  VerticalFlex,
} from '@components/RecordCard/RecordCard.style';
import RoundedButton from '@components/RoundedButton/RoundedButton';
import {
  AppointmentTooltipCommandButton,
  AppointmentFormCommandButton,
  DateEditor,
  Label,
  NavigationButton,
  Overlay,
  RadioGroup,
  Select,
  TextEditor,
  TodayButton, // CalendarOverlay,
  OpenButton,
  ViewSwitcher,
  AppointmentTooltipContent,
  Appointment,
  EditRecurrenceMenuLayout,
  ConfirmationDialogLayout,
  ConfirmationDialogOverlay,
  RecurrenceMenu,
  CommandLayout,
  BooleanEditor,
  WeeklyRecurrenceSelector,
  RoutineBaseForm,
  DayViewTimeTableCell,
  WeekViewTimeTableCell,
  MonthViewTimeTableCell,
  EditRecurrenceMenuOverlay,
  WeekDayCell,
  MonthDayCell,
} from '@components/RoutineScheduler';
import {
  Circle,
  TimeIndicator,
} from '@components/RoutineScheduler/AppointmentForm/TimeIndicator/TimeIndicator.style';
import Status from '@components/Status';

import {
  useDeleteRoutine,
  usePostRoutine,
  useUpdateRoutine,
} from '@hooks/api/routine';
import { useApp } from '@hooks/useApp';
import { useSession } from '@hooks/useSession';
import { useSnackbar } from '@hooks/useSnackbar';

import { locale } from '@intl/index';

import type { RoutineEvent } from '@models/routine';
import { RoutineRecord } from '@models/routine';

import { isValidDate, roundToNearestMinutes } from '@utils/date';
import { queuePromises } from '@utils/queuePromises';

import type { TaskModelInput } from 'generated/types';

const messages = defineMessages({
  crudSuccess: {
    id: 'routineScheduler.crudSuccess',
    defaultMessage: 'Routine successfully {type}',
  },
  crudError: {
    id: 'routineScheduler.crudError',
    defaultMessage: 'Routine could not be {type}',
  },
  added: {
    id: 'routineScheduler.added',
    defaultMessage: 'added',
  },
  updated: {
    id: 'routineScheduler.updated',
    defaultMessage: 'updated',
  },
  deleted: {
    id: 'routineScheduler.deleted',
    defaultMessage: 'deleted',
  },
  errorTimeSlotsRange: {
    id: 'routineScheduler.errorTimeSlotsRange',
    defaultMessage: 'Start time cannot be after end time',
  },
  errorTimeSlotsBlank: {
    id: 'routineScheduler.errorTimeSlotsBlank',
    defaultMessage: 'Times cannot be blank',
  },
  errorStartTimeSlotBlank: {
    id: 'routineScheduler.errorStartTimeSlotBlank',
    defaultMessage: 'Start time cannot be blank',
  },
  errorEndTimeSlotBlank: {
    id: 'routineScheduler.errorEndTimeSlotBlank',
    defaultMessage: 'End time cannot be blank',
  },
  errorStartDate: {
    id: 'routineScheduler.errorStartDate',
    defaultMessage: 'Start date cannot be blank',
  },
  errorUntilDate: {
    id: 'routineScheduler.errorUntilDate',
    defaultMessage: 'End date cannot be blank',
  },
  errorWeekdays: {
    id: 'routineScheduler.errorWeekdays',
    defaultMessage: 'A weekday must be selected for weekly recurrences',
  },
  noTitle: {
    id: 'routineScheduler.noTitle',
    defaultMessage: '(No Title)',
  },
  newRoutine: {
    id: 'routineScheduler.newRoutine',
    defaultMessage: 'New Routine',
  },
  completed: {
    id: 'GLOBAL.completed',
    defaultMessage: 'Completed',
  },
  notCompleted: {
    id: 'GLOBAL.notCompleted',
    defaultMessage: 'Not completed',
  },
  upcoming: {
    id: 'routineScheduler.upcoming',
    defaultMessage: 'Upcoming',
  },
});

const overrideMessages = {
  'en-US': {
    endRepeatLabel: 'Ends',
  },
};

const getMessages = (locale: 'en-US') => overrideMessages[locale];

const getDefaultAddedAppointment = (): RoutineEvent => {
  const now = roundToNearestMinutes(new Date(), 15);
  return {
    startDate: now,
    recurrenceStartDate: now,
    endDate: addMinutes(now, 30),
    timeSlots: [
      {
        startTime: now,
        endTime: addMinutes(now, 30),
      },
    ],
    rRule: 'RRULE:INTERVAL=1;FREQ=DAILY;COUNT=30',
  };
};

const BasicLayout = ({ ...props }: AppointmentForm.BasicLayoutProps) => {
  const { appointmentData } = props;
  const routineData = appointmentData as RoutineEvent;
  return <RoutineBaseForm routineData={routineData} {...props} />;
};

const RecurringIcon = () => {
  return <Icon color="text.secondary" name="repeat" type="far" />;
};

const WeeklySelector = ({
  ...props
}: AppointmentForm.WeeklyRecurrenceSelectorProps) => {
  return <WeeklyRecurrenceSelector {...props} />;
};

const Indicator = React.forwardRef<
  HTMLDivElement | null,
  DxCurrentTimeIndicator.IndicatorProps
>(({ ...props }, ref) => {
  return (
    <TimeIndicator {...props} ref={ref}>
      <Circle />
    </TimeIndicator>
  );
});

function RoutineScheduler({
  date = new Date(),
  scheduledRoutines = [],
  view = 'Day',
  shadePreviousCells = true,
  shadePreviousEvents = true,
  updateInterval = 10000,
}: {
  /**
   * Date that should appear on the routine calendar
   */
  date: Date;
  /**
   * The user's routines
   */
  scheduledRoutines: RoutineEvent[];
  /**
   * Detail view of the calendar
   */
  view: 'Day' | 'Month' | 'Week';
  /**
   * Should cells before the current date be shaded?
   */
  shadePreviousCells?: boolean;
  /**
   * Should past routine events be shaded?
   */
  shadePreviousEvents?: boolean;
  /**
   * How often to update the current time marker on the calendar
   */
  updateInterval?: number;
}) {
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const {
    state: { activeProfile },
  } = useSession();
  const [patient_id] = [activeProfile?.patient_id];
  const {
    state: { routine: selectedRoutine, isSingleRoutine },
    selectRoutine,
    setIsSingleRoutine,
  } = useApp();
  const [loading, setLoading] = useState(false);
  const [appointmentFormState, setAppointmentFormState] = useState<{
    addedAppointmentData: AppointmentModel | undefined;
    visible: boolean | undefined;
  }>({ addedAppointmentData: undefined, visible: undefined });
  const [currentDate, setCurrentDate] = useState<Date>(date);
  const [currentView, setCurrentView] = useState<'Day' | 'Month' | 'Week'>(
    view
  );
  const [postRoutineRecord] = usePostRoutine();
  const [updateRoutineRecord] = useUpdateRoutine();
  const [deleteRoutineRecord] = useDeleteRoutine();

  const { palette } = useTheme();

  const indicatorRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (indicatorRef.current) {
      indicatorRef.current.scrollIntoView({ block: 'center' });
    }
  }, [loading]);

  useEffect(() => {
    if (selectedRoutine) {
      setAppointmentFormState({
        addedAppointmentData: selectedRoutine,
        visible: true,
      });
    }
    return () => {
      selectRoutine({
        routine: undefined,
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRoutine]);

  /**
   * Add a new routine record
   * @param addedRoutine A dictionary of containing the fields that the user has filled out on the appointment form
   * @returns An object with a boolean error status
   */
  const addRoutine = async (
    addedRoutine: RoutineEvent,
    isSingleOccurrence?: boolean
  ) => {
    if (!patient_id) {
      return { error: true, message: formatMessage(messages.added) };
    }
    if (addedRoutine.title === undefined || addedRoutine.title.trim() === '') {
      addedRoutine.title = formatMessage(messages.noTitle);
    }
    try {
      const model = RoutineRecord.convertEventToModel(
        addedRoutine,
        patient_id,
        isSingleOccurrence
      );
      await postRoutineRecord({ input: model });
    } catch (e) {
      Sentry.captureException(e);
      setLoading(false);
      enqueueSnackbar({
        variant: 'error',
        message: formatMessage(messages.crudError, {
          type: formatMessage(messages.added),
        }),
      });
      return { message: formatMessage(messages.added), error: true };
    }

    return { error: false, message: formatMessage(messages.added) };
  };

  /**
   * Update existing routine record(s)
   * @param changed object with key <Routine ID>, and value <Changed props>
   * @param added Routine event created as a result of updating a routine
   * @param allRoutines All routines displayed on the scheduler
   * @returns An object with a boolean error status
   */
  const updateRoutines = async (
    changed: Record<string, Partial<RoutineEvent>>,
    allRoutines: RoutineEvent[]
  ) => {
    if (!patient_id) {
      return { error: true, message: formatMessage(messages.updated) };
    }
    try {
      const updatedRoutines = Object.keys(changed).reduce<TaskModelInput[]>(
        (acc, curr) => {
          const routine = allRoutines.find(({ id }) => id === curr);
          if (routine && patient_id) {
            const model = RoutineRecord.convertEventToModel(
              {
                ...routine,
                ...changed[curr],
                id: curr,
              },
              patient_id
            );
            acc.push(model);
          }
          return acc;
        },
        []
      );
      await queuePromises(updatedRoutines, async (routine) =>
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        updateRoutineRecord({ input: { task: routine, task_id: routine.id! } })
      );
    } catch (e) {
      Sentry.captureException(e);
      setLoading(false);
      enqueueSnackbar({
        variant: 'error',
        message: formatMessage(messages.crudError, {
          type: formatMessage(messages.updated),
        }),
      });
      return { error: true, message: formatMessage(messages.updated) };
    }
    return { error: false, message: formatMessage(messages.updated) };
  };

  /**
   * Delete a routine record
   * @param deleted Instance link UUID of the deleted routine
   * @param allRoutines All routines displayed on the scheduler
   * @returns An object with a boolean error status
   */
  const deleteRoutine = async (
    deleted: number | string,
    allRoutines: RoutineEvent[]
  ) => {
    if (!patient_id) {
      return { error: true, message: formatMessage(messages.deleted) };
    }
    const taskId = allRoutines
      .find((routine) => routine.id === deleted)
      ?.id?.toString();
    if (!taskId) {
      return { error: true, message: formatMessage(messages.deleted) };
    }
    try {
      await deleteRoutineRecord({ input: { task_id: taskId } });
    } catch (e) {
      Sentry.captureException(e);
      setLoading(false);
      enqueueSnackbar({
        variant: 'error',
        message: formatMessage(messages.crudError, {
          type: formatMessage(messages.deleted),
        }),
      });
      return { error: true, message: formatMessage(messages.deleted) };
    }
    return {
      error: false,
      message: formatMessage(messages.deleted),
    };
  };

  /**
   * Validate the fields in a routine event
   * @param routineEvent Routine event
   * @description Validate the fields in a routine event, and display an error modal containing messages about the incorrect fields
   */
  const validateRoutine = (routineEvent: RoutineEvent) => {
    const { startDate, rRule } = routineEvent;
    const timeSlots = RoutineRecord.getTimeSlots(routineEvent);
    let valid = true;

    // Validate time slots
    if (timeSlots.length) {
      for (const slot of timeSlots) {
        if (dayjs(slot.endTime).isBefore(slot.startTime)) {
          valid = false;
          enqueueSnackbar({
            variant: 'error',
            message: formatMessage(messages.errorTimeSlotsRange),
          });
        }
        if (!isValidDate(slot.startTime)) {
          valid = false;
          enqueueSnackbar({
            variant: 'error',
            message: formatMessage(messages.errorStartTimeSlotBlank),
          });
        }
        if (!isValidDate(slot.endTime)) {
          valid = false;
          enqueueSnackbar({
            variant: 'error',
            message: formatMessage(messages.errorEndTimeSlotBlank),
          });
        }
      }
    } else {
      valid = false;
      enqueueSnackbar({
        variant: 'error',
        message: formatMessage(messages.errorTimeSlotsBlank),
      });
    }

    // Validate start date
    if (!isValidDate(new Date(startDate))) {
      valid = false;
      enqueueSnackbar({
        variant: 'error',
        message: formatMessage(messages.errorStartDate),
      });
    }

    if (rRule) {
      const { options } = RRule.fromString(rRule);
      const { byweekday, freq } = options;
      // Validate weekly recurrence options
      if (freq === RRule.WEEKLY && !byweekday.length) {
        valid = false;
        enqueueSnackbar({
          variant: 'error',
          message: formatMessage(messages.errorWeekdays),
        });
      }
    }

    return valid;
  };

  /**
   * Commit the changes made by the appointment form
   * @param changes Object containing added, modified, and deleted routine information
   */
  const commitChanges = async (changes: ChangeSet) => {
    setLoading(true);
    const { added, changed, deleted } = changes;
    const changedRoutine = changed as Record<string, Partial<RoutineEvent>>;
    const allRoutines = scheduledRoutines;
    const editPromises = [];
    let crudType = '';
    // New routine added
    if (added) {
      const addedRoutine = {
        ...(added as RoutineEvent),
      };
      const valid = validateRoutine(addedRoutine);
      if (!valid) {
        setLoading(false);
        return;
      }
      crudType = formatMessage(messages.added);
      editPromises.push(addRoutine(addedRoutine, isSingleRoutine));
    }

    // Existing routine deleted
    if (deleted !== undefined) {
      crudType = formatMessage(messages.deleted);
      editPromises.push(deleteRoutine(deleted, allRoutines));
    }

    // Existing routine modified
    if (changed !== undefined && Object.keys(changedRoutine).length !== 0) {
      for (const routine of allRoutines) {
        if (routine.id && changed[routine.id]) {
          const routineEvent = {
            ...routine,
            ...changedRoutine[routine.id],
          };
          const valid = validateRoutine(routineEvent);
          if (!valid) {
            setLoading(false);
            return;
          }
        }
      }
      crudType = formatMessage(messages.updated);
      editPromises.push(updateRoutines(changed, allRoutines));
    }

    const responses = await Promise.all(editPromises);
    const errors = responses.filter((response) => response.error);
    setAppointmentFormState({
      addedAppointmentData: undefined,
      visible: false,
    });
    selectRoutine({
      routine: undefined,
    });
    setIsSingleRoutine({ isSingleRoutine: undefined });
    setLoading(false);
    // If something changed and there were no errors, show success message
    if ((added || changed || deleted) && errors.length === 0) {
      enqueueSnackbar({
        variant: 'success',
        message: formatMessage(messages.crudSuccess, { type: crudType }),
      });
    } else {
      enqueueSnackbar({
        variant: 'error',
        message: formatMessage(messages.crudError, {
          type: errors.at(0)?.message ?? formatMessage(messages.updated),
        }),
      });
    }
  };

  // const MemoizedCalendarOverlay = React.useCallback(
  //   (props: DxDateNavigator.OverlayProps) => {
  //     return (
  //       <CalendarOverlay
  //         currentDate={currentDate}
  //         onDateChange={setCurrentDate}
  //         {...props}
  //       />
  //     );
  //   },
  //   [currentDate]
  // );

  return (
    <ContentLoader loading={loading}>
      <HorizontalFlex
        justifyContent="space-between"
        marginBottom={0}
        marginTop={0}
        width="100%"
      >
        <VerticalFlex alignItems="flex-start">
          <RoundedButton
            autoSize
            fontSize={14}
            height={32}
            iconName="plus"
            onClick={() => {
              const now = roundToNearestMinutes(new Date(), 15);
              const event: RoutineEvent = {
                startDate: now,
                endDate: addMinutes(now, 30),
                rRule: `RRULE:INTERVAL=1;FREQ=DAILY;COUNT=30`,
              };
              selectRoutine({
                routine: event,
              });
            }}
          >
            {formatMessage(messages.newRoutine)}
          </RoundedButton>
        </VerticalFlex>
        <VerticalFlex alignItems="flex-end">
          <Status
            helperText={formatMessage(messages.completed)}
            variant="green"
          />
          <Status
            helperText={formatMessage(messages.notCompleted)}
            variant="danger"
          />
          <Status
            helperText={formatMessage(messages.upcoming)}
            variant="blue"
          />
        </VerticalFlex>
      </HorizontalFlex>
      <OuterContainer
        clickable={false}
        color={palette.background.paper}
        style={{
          padding: '2px 0 2px 1px',
          fontWeight: 400,
          height: 'calc(100% - 52px)',
        }}
      >
        <Scheduler data={scheduledRoutines} firstDayOfWeek={0} height="auto">
          <ViewState
            currentDate={currentDate}
            currentViewName={currentView}
            onCurrentDateChange={(date) => setCurrentDate(date)}
            onCurrentViewNameChange={(name: string) => {
              if (name === 'Day' || name === 'Month' || name === 'Week') {
                setCurrentView(name);
                sessionStorage.setItem('calendarView', name);
              }
            }}
          />
          <EditingState
            addedAppointment={appointmentFormState.addedAppointmentData}
            defaultAddedAppointment={getDefaultAddedAppointment()}
            onAddedAppointmentChange={(addedAppointment) =>
              setAppointmentFormState({
                ...appointmentFormState,
                addedAppointmentData: addedAppointment as AppointmentModel,
              })
            }
            onCommitChanges={commitChanges}
          />
          <EditRecurrenceMenu
            layoutComponent={EditRecurrenceMenuLayout}
            overlayComponent={EditRecurrenceMenuOverlay}
          />
          <WeekView
            dayScaleCellComponent={WeekDayCell}
            endDayHour={24}
            startDayHour={0}
            timeTableCellComponent={WeekViewTimeTableCell}
          />
          <MonthView
            dayScaleCellComponent={MonthDayCell}
            timeTableCellComponent={MonthViewTimeTableCell}
          />
          <DayView timeTableCellComponent={DayViewTimeTableCell} />
          <Toolbar />
          <DxViewSwitcher switcherComponent={ViewSwitcher} />
          <DxDateNavigator
            navigationButtonComponent={NavigationButton}
            openButtonComponent={OpenButton}
            // overlayComponent={MemoizedCalendarOverlay}
          />
          <DxTodayButton buttonComponent={TodayButton} />
          <Appointments
            appointmentComponent={Appointment}
            recurringIconComponent={RecurringIcon}
          />
          <DxConfirmationDialog
            ignoreDelete
            layoutComponent={ConfirmationDialogLayout}
            overlayComponent={ConfirmationDialogOverlay}
          />
          <AppointmentTooltip
            commandButtonComponent={AppointmentTooltipCommandButton}
            contentComponent={AppointmentTooltipContent}
            showDeleteButton
            showOpenButton
          />
          <AppointmentForm
            appointmentData={appointmentFormState.addedAppointmentData}
            basicLayoutComponent={BasicLayout}
            booleanEditorComponent={BooleanEditor}
            commandButtonComponent={AppointmentFormCommandButton}
            commandLayoutComponent={CommandLayout}
            dateEditorComponent={DateEditor}
            labelComponent={Label}
            messages={getMessages(locale)}
            onVisibilityChange={(visible) => {
              if (visible) {
                setAppointmentFormState({
                  ...appointmentFormState,
                  visible: true,
                });
              } else {
                setAppointmentFormState({
                  addedAppointmentData: undefined,
                  visible: false,
                });
                selectRoutine({
                  routine: undefined,
                });
              }
            }}
            overlayComponent={Overlay}
            radioGroupComponent={RadioGroup}
            recurrenceLayoutComponent={RecurrenceMenu}
            selectComponent={Select}
            textEditorComponent={TextEditor}
            visible={appointmentFormState.visible}
            weeklyRecurrenceSelectorComponent={WeeklySelector}
          />
          <DxCurrentTimeIndicator
            indicatorComponent={Indicator}
            shadePreviousAppointments={shadePreviousEvents}
            shadePreviousCells={shadePreviousCells}
            updateInterval={updateInterval}
          />
        </Scheduler>
      </OuterContainer>
    </ContentLoader>
  );
}

export default RoutineScheduler;
